import { useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { flattenSectionTree } from '../lib/sectionTree';

/** Minimal section shape the picker needs (a TodoSectionResponse satisfies it,
 *  as does a personal board section adapted to these four fields). */
export interface SelectableSection {
  sectionId: string;
  parentSectionId: string | null;
  sortOrder: number;
  name: string;
}

interface SectionSelectProps {
  sections: SelectableSection[];
  /** `'none'` for the null option, else a section id. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Label for the null/root option — "Unsorted" when landing a task, "(Top
   * level)" when re-parenting a section. */
  noneLabel?: string;
  /** Whether to offer the null/root ("none") option. Hidden for a member who
   * may not move a task to Unsorted. Defaults to true. */
  showNone?: boolean;
  id?: string;
}

/**
 * An indented, tree-ordered Section picker — sections are flattened in the SAME
 * order the list renders them (pre-order DFS by sortOrder) and indented by depth
 * so the hierarchy reads at a glance. Shared by the item editor and the
 * cross-list move dialog; the `noneLabel` distinguishes "land in Unsorted" from
 * "attach at the top level".
 */
export function SectionSelect({
  sections,
  value,
  onChange,
  disabled,
  noneLabel = 'Unsorted',
  showNone = true,
  id,
}: SectionSelectProps) {
  const ordered = useMemo(() => flattenSectionTree(sections), [sections]);
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger id={id}>
        <SelectValue placeholder={noneLabel} />
      </SelectTrigger>
      <SelectContent>
        {showNone && <SelectItem value="none">{noneLabel}</SelectItem>}
        {ordered.map(({ section, depth }) => (
          <SelectItem key={section.sectionId} value={section.sectionId}>
            {/* Indent nested sections so the tree structure reads at a glance;
                the trigger still shows the plain name. */}
            <span style={{ paddingLeft: `${depth}rem` }}>{section.name}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
